import { forwardRef, useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import clsx from 'clsx';
import './Field.css';

/* ------------------------------------------------------------------ *
 * Form controls read as fields on a printed form.
 *
 * No floating labels. They are the single most recognisable Material
 * tell, they break the moment a Spanish label is longer than its
 * input, and they hide the label exactly when the user is typing and
 * most needs it. Labels sit above, stencilled, always visible.
 * ------------------------------------------------------------------ */

interface FieldProps {
  label: ReactNode;
  /** Rendered beside the label, e.g. "optional" or a unit. */
  hint?: ReactNode;
  error?: string | null;
  /** Explanatory text under the control. Not a substitute for a clear label. */
  description?: ReactNode;
  required?: boolean;
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string;
}

export function Field({
  label,
  hint,
  error,
  description,
  required,
  children,
  className,
}: FieldProps) {
  const id = useId();
  const descId = `${id}-desc`;
  const errId = `${id}-err`;
  const describedBy = [description ? descId : null, error ? errId : null]
    .filter(Boolean)
    .join(' ') || undefined;

  return (
    <div className={clsx('pd-field', className)} data-invalid={error ? true : undefined}>
      <div className="pd-field__head">
        <label className="stencil pd-field__label" htmlFor={id}>
          {label}
          {required ? <span className="pd-field__req" aria-hidden="true">*</span> : null}
        </label>
        {hint ? <span className="pd-field__hint">{hint}</span> : null}
      </div>

      {children({ id, describedBy, invalid: Boolean(error) })}

      {description ? (
        <p id={descId} className="pd-field__desc">
          {description}
        </p>
      ) : null}
      {error ? (
        <p id={errId} className="pd-field__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/* ---------------- Input ---------------- */

/**
 * `prefix` is omitted from the HTML attributes because the global
 * `prefix` attribute is a string and ours is a node — an RDFa leftover
 * that would otherwise force every affix to be plain text.
 */
interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  invalid?: boolean | undefined;
  /** Renders in mono with tabular figures — every quantity and price input. */
  numeric?: boolean | undefined;
  /** Fixed affixes inside the box: a unit, a currency, a percent sign. */
  suffix?: ReactNode;
  prefix?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, numeric, suffix, prefix, className, ...rest },
  ref,
) {
  const control = (
    <input
      ref={ref}
      className={clsx('pd-input', numeric && 'num', !suffix && !prefix && className)}
      data-invalid={invalid || undefined}
      data-numeric={numeric || undefined}
      aria-invalid={invalid || undefined}
      inputMode={numeric ? 'decimal' : undefined}
      {...rest}
    />
  );

  if (!suffix && !prefix) return control;

  return (
    <div className={clsx('pd-input-group', className)} data-invalid={invalid || undefined}>
      {prefix ? <span className="pd-input-group__affix">{prefix}</span> : null}
      {control}
      {suffix ? <span className="pd-input-group__affix">{suffix}</span> : null}
    </div>
  );
});

/* ---------------- Textarea ---------------- */

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function Textarea({ invalid, className, rows = 3, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={clsx('pd-input pd-textarea', className)}
      data-invalid={invalid || undefined}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
});

/* ---------------- Native select ----------------
   Used wherever the option list is short and static. Anything
   searchable uses the Combobox instead. */

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }
>(function Select({ invalid, className, children, ...rest }, ref) {
  return (
    <div className={clsx('pd-select', className)}>
      <select
        ref={ref}
        className="pd-input pd-select__control"
        data-invalid={invalid || undefined}
        aria-invalid={invalid || undefined}
        {...rest}
      >
        {children}
      </select>
      <span className="pd-select__mark" aria-hidden="true">
        ▾
      </span>
    </div>
  );
});

/* ---------------- Segmented control ----------------
   Replaces radio groups and most tab bars. Reads as a row of stamped
   keys, which is the interaction it is standing in for. */

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  size = 'md',
  label,
  className,
}: {
  value: T;
  onChange: (next: T) => void;
  options: readonly { value: T; label: ReactNode; title?: string; disabled?: boolean }[];
  size?: 'sm' | 'md';
  label: string;
  className?: string;
}) {
  return (
    <div
      className={clsx('pd-segmented', className)}
      role="radiogroup"
      aria-label={label}
      data-size={size}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          disabled={o.disabled}
          title={o.title}
          className="pd-segmented__item"
          data-active={o.value === value || undefined}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ---------------- Checkbox & switch ---------------- */

export const Checkbox = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }
>(function Checkbox({ label, className, ...rest }, ref) {
  return (
    <label className={clsx('pd-checkbox', className)}>
      <input ref={ref} type="checkbox" className="pd-check" {...rest} />
      <span className="pd-checkbox__label">{label}</span>
    </label>
  );
});

export function Switch({
  checked,
  onChange,
  label,
  disabled,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={clsx('pd-switch', className)}
      onClick={() => onChange(!checked)}
    >
      <span className="pd-switch__track">
        <span className="pd-switch__knob" />
      </span>
    </button>
  );
}
